import { useEffect } from "react"
import { createFileRoute, Link } from "@tanstack/react-router"
import { ArrowLeft, ArrowUpRight, Check, Package, Rocket, ShieldCheck, Sparkles, Tag } from "lucide-react"
import { useProduct, useProducts } from "@/hooks/useProducts"
import { ErrorState } from "@/components/state"
import { pageMeta, syncMetaTags } from "@/lib/seo"
import { JsonLd } from "@/components/json-ld"
import { FloatingIcon } from "@/components/floating-icon"

function titleCase(slug: string) {
  return slug.replace(/-/g, " ").replace(/\b\w/g, (c) => c.toUpperCase())
}

/**
 * price_label is free text an admin types ("Starting at 4000BDT/mo",
 * "Custom pricing", "Contact us", ...) — schema.org's Offer.price expects
 * a plain number, so pulling that text in directly would ship invalid
 * structured data for anything that isn't a clean digit string. Only
 * emit an Offer when a number can actually be pulled out of it.
 */
function extractPrice(label: string): string | null {
  const match = label.match(/[\d,]+(?:\.\d+)?/)
  return match ? match[0].replace(/,/g, "") : null
}

export const Route = createFileRoute("/_public/products/$slug")({
  component: ProductDetail,
  // Same pattern as the blog post route: this page fetches its data
  // client-side (useProduct), so head() only ever sees the slug param —
  // it renders a reasonable placeholder immediately, corrected once the
  // real product loads (see the title/description below).
  head: ({ params }) =>
    pageMeta({
      title: `${titleCase(params.slug)} — Nexovia IT Products`,
      description: "See what this Nexovia IT product does and how to get started.",
      path: `/products/${params.slug}`,
    }),
})

function ProductDetail() {
  const { slug } = Route.useParams()
  const { data: product, isLoading, isError } = useProduct(slug)
  const { data: allProducts } = useProducts()

  const moreProducts = (allProducts ?? [])
    .filter((p) => p.slug !== slug && p.is_active)
    .slice(0, 3)

  const pageUrl = typeof window !== "undefined" ? window.location.href : ""

  useEffect(() => {
    if (!product) return
    syncMetaTags({
      title: `${product.title} — Nexovia IT Products`,
      description: product.description ?? product.tagline ?? undefined,
      image: product.image_url ?? undefined,
    })
  }, [product])

  return (
    <>
      {product && (
        <JsonLd
          data={{
            "@context": "https://schema.org",
            "@type": "Product",
            name: product.title,
            ...(product.tagline && { description: product.tagline }),
            ...(product.image_url && { image: [product.image_url] }),
            brand: { "@type": "Organization", name: "Nexovia IT" },
            ...(product.price_label &&
              extractPrice(product.price_label) && {
                offers: {
                  "@type": "Offer",
                  price: extractPrice(product.price_label),
                  priceCurrency: "BDT",
                  url: pageUrl,
                  availability: "https://schema.org/InStock",
                },
              }),
          }}
        />
      )}

      <section className="border-b border-border">
        <div className="mx-auto max-w-5xl px-6 py-6">
          <Link to="/products" className="inline-flex items-center gap-2 text-sm font-semibold text-muted-foreground hover:text-rust">
            <ArrowLeft className="h-4 w-4" /> Back to products
          </Link>
        </div>
      </section>

      {isLoading ? (
        <div className="mx-auto max-w-5xl px-6 py-20 md:py-24 space-y-4">
          <div className="h-4 w-1/4 animate-pulse rounded bg-secondary" />
          <div className="h-10 w-3/4 animate-pulse rounded bg-secondary" />
          <div className="aspect-[16/9] animate-pulse rounded-2xl bg-secondary" />
        </div>
      ) : isError || !product ? (
        <div className="mx-auto max-w-4xl px-6 py-20 md:py-24">
          <ErrorState message="Couldn't load this product — it may not exist." />
        </div>
      ) : (
        <>
          {/* HERO + IMAGE, one continuous section so the product visual
              feels like part of the hero rather than a separate block
              bolted on below a divider. */}
          <section className="relative overflow-hidden bg-background">
            <div
              className="absolute inset-0 -z-10"
              style={{
                background:
                  "radial-gradient(ellipse at 50% 20%, color-mix(in oklab, var(--rust-glow) 22%, transparent), transparent 55%), radial-gradient(ellipse at 90% 0%, color-mix(in oklab, var(--rust) 14%, transparent), transparent 55%)",
              }}
            />
            <div
              className="absolute inset-0 -z-10 opacity-40"
              style={{
                backgroundImage:
                  "linear-gradient(color-mix(in oklab, var(--ink) 6%, transparent) 1px, transparent 1px), linear-gradient(90deg, color-mix(in oklab, var(--ink) 6%, transparent) 1px, transparent 1px)",
                backgroundSize: "48px 48px",
              }}
            />

            <FloatingIcon icon={Package} className="left-[6%] top-[12%] hidden lg:flex" delay={0} />
            <FloatingIcon icon={ShieldCheck} className="right-[7%] top-[16%] hidden lg:flex" delay={1} />
            <FloatingIcon icon={Sparkles} className="left-[10%] top-[64%] hidden lg:flex" delay={2} />
            <FloatingIcon icon={Rocket} className="right-[9%] top-[60%] hidden lg:flex" delay={1.6} />

            <div className="relative mx-auto max-w-4xl px-6 pt-16 md:pt-20 text-center">
              {product.tagline && (
                <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background/70 px-4 py-1.5 text-xs font-medium uppercase tracking-widest text-rust backdrop-blur">
                  <span className="h-1.5 w-1.5 rounded-full bg-rust" /> {product.tagline}
                </span>
              )}
              <h1 className="mx-auto mt-6 max-w-3xl text-4xl md:text-6xl font-bold text-balance">{product.title}</h1>
              {product.description && (
                <p className="mx-auto mt-6 max-w-2xl text-lg text-muted-foreground">{product.description}</p>
              )}
              <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
                {product.price_label && (
                  <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-4 py-2.5 text-sm font-semibold shadow-card">
                    <Tag className="h-4 w-4 text-rust" /> {product.price_label}
                  </span>
                )}
                {product.cta_link && (
                  <a
                    href={product.cta_link}
                    target={product.cta_link.startsWith("http") ? "_blank" : undefined}
                    rel={product.cta_link.startsWith("http") ? "noreferrer" : undefined}
                    className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant hover:opacity-95"
                  >
                    {product.cta_text || "Get started"} <ArrowUpRight className="h-4 w-4" />
                  </a>
                )}
              </div>
            </div>

            <div className="relative mx-auto max-w-4xl px-6 pb-16 pt-10 md:pb-20 md:pt-14">
              <div className="relative aspect-[16/9] overflow-hidden rounded-3xl border border-border bg-gradient-hero shadow-elegant">
                {product.image_url ? (
                  // No object-top here on purpose: unlike headshots/blog covers,
                  // these are marketing graphics with the logo+title centered
                  // vertically — anchoring to the top crops the title off the
                  // bottom instead of the (correct, symmetric) whitespace.
                  <img
                    src={product.image_url}
                    alt={product.title}
                    width={1600}
                    height={900}
                    className="h-full w-full object-cover"
                  />
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <Package className="h-12 w-12 text-cream/80" />
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* DETAILS */}
          {product.details_content && (
            <section className="mx-auto max-w-4xl px-6 py-16 md:py-20">
              <div
                className="prose prose-neutral max-w-none prose-headings:font-display prose-a:text-rust"
                dangerouslySetInnerHTML={{ __html: product.details_content }}
              />
            </section>
          )}

          {/* FEATURES */}
          {product.features.length > 0 && (
            <section className="mx-auto max-w-4xl px-6 py-16 md:py-20">
              <h2 className="text-2xl md:text-3xl font-bold">What's included</h2>
              <ul className="mt-8 grid gap-4 sm:grid-cols-2">
                {product.features.map((feature) => (
                  <li
                    key={feature}
                    className="flex items-start gap-3 rounded-2xl border border-border bg-card p-4"
                  >
                    <Check className="mt-0.5 h-5 w-5 shrink-0 text-rust" />
                    <span className="text-sm leading-relaxed">{feature}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {/* MORE PRODUCTS */}
          {moreProducts.length > 0 && (
            <section className="border-t border-border bg-secondary/30">
              <div className="mx-auto max-w-7xl px-6 py-24 md:py-28">
                <div className="text-center">
                  <p className="text-xs font-semibold uppercase tracking-widest text-rust">Explore more</p>
                  <h2 className="mt-3 text-3xl md:text-4xl font-bold">Other Products We Build.</h2>
                </div>
                <div className="mt-14 grid gap-6 md:grid-cols-3">
                  {moreProducts.map((p) => (
                    <Link
                      key={p.id}
                      to="/products/$slug"
                      params={{ slug: p.slug }}
                      className="group flex flex-col overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-1 hover:shadow-card"
                    >
                      <div className="relative aspect-[16/10] overflow-hidden bg-gradient-hero">
                        {p.image_url ? (
                          <img
                            src={p.image_url}
                            alt={p.title}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div className="flex h-full w-full items-center justify-center">
                            <Package className="h-10 w-10 text-cream/80" />
                          </div>
                        )}
                      </div>
                      <div className="flex flex-1 flex-col p-6">
                        {p.tagline && <span className="text-xs font-semibold uppercase tracking-widest text-rust">{p.tagline}</span>}
                        <h3 className="mt-2 text-lg font-semibold">{p.title}</h3>
                        <span className="mt-4 inline-flex items-center gap-2 text-sm font-semibold text-ink group-hover:text-rust">
                          See Details <ArrowUpRight className="h-4 w-4" />
                        </span>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}

          {/* CTA */}
          <section className="mx-auto max-w-5xl px-6 py-24 md:py-28">
            <div className="rounded-3xl border border-border bg-card p-10 md:p-16 text-center shadow-card">
              <p className="text-xs font-semibold uppercase tracking-widest text-rust">Something custom?</p>
              <h2 className="mt-3 text-3xl md:text-4xl font-bold">We Also Build Bespoke Products.</h2>
              <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
                If this doesn't quite fit, we can design and build a product tailored to your business from the ground up.
              </p>
              <div className="mt-8 flex flex-wrap justify-center gap-3">
                <Link to="/contact" className="inline-flex items-center gap-2 rounded-full bg-gradient-accent px-6 py-3 text-sm font-semibold text-cream shadow-elegant">
                  Start a project <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </section>
        </>
      )}
    </>
  )
}
